import type { Product } from "./types";

export interface SubcategoryDefinition {
  slug: string;
  ar: string;
  en: string;
}

export interface CategoryDefinition {
  slug: string;
  ar: string;
  en: string;
  descriptionAr: string;
  descriptionEn: string;
  subcategories: SubcategoryDefinition[];
}

export const categoryDefinitions: CategoryDefinition[] = [
  {
    slug: "supplements",
    ar: "المكملات والفيتامينات",
    en: "Supplements",
    descriptionAr:
      "فيتامينات ومعادن ومكملات غذائية مصنفة بحسب الاحتياج لسهولة الاستفسار عنها.",
    descriptionEn:
      "Vitamins, minerals and nutritional supplements organized by need for easier inquiry.",
    subcategories: [
      [
        "nutritional-supplements",
        "المكملات الغذائية",
        "Nutritional Supplements",
      ],
      ["vitamins", "الفيتامينات", "Vitamins"],
      ["minerals", "المعادن", "Minerals"],
      ["multivitamins", "الفيتامينات المتعددة", "Multivitamins"],
      ["omega-fatty-acids", "الأوميغا والأحماض الدهنية", "Omega & Fatty Acids"],
      ["amino-acids", "الأحماض الأمينية", "Amino Acids"],
      ["herbal-supplements", "المكملات العشبية", "Herbal Supplements"],
      ["sports-supplements", "المكملات الرياضية", "Sports Supplements"],
      ["kids-supplements", "مكملات الأطفال", "Kids Supplements"],
      ["energy-supplements", "مكملات الطاقة", "Energy Supplements"],
      ["bone-health", "صحة العظام", "Bone Health"],
      ["immune-support", "دعم المناعة", "Immune Support"],
      ["general-wellness", "الصحة العامة", "General Wellness"],
      [
        "healthy-foods-herbal-tea",
        "الشاي والأغذية الصحية",
        "Healthy Foods & Herbal Tea",
      ],
    ].map(([slug, ar, en]) => ({ slug, ar, en })),
  },
  {
    slug: "beauty-care",
    ar: "العناية والجمال",
    en: "Care & Beauty",
    descriptionAr:
      "منتجات العناية بالبشرة والشعر والجسم والعناية الشخصية اليومية.",
    descriptionEn: "Skin, hair, body and everyday personal-care products.",
    subcategories: [
      ["skin-care", "العناية بالبشرة", "Skin Care"],
      ["face-care", "العناية بالوجه", "Face Care"],
      ["body-care", "العناية بالجسم", "Body Care"],
      ["hair-care", "العناية بالشعر", "Hair Care"],
      ["lip-care", "العناية بالشفاه", "Lip Care"],
      ["makeup-removal", "إزالة المكياج", "Makeup Removal"],
      ["makeup-beauty", "المكياج والجمال", "Makeup & Beauty"],
      ["personal-care", "العناية الشخصية", "Personal Care"],
      ["eye-care", "العناية بالعين", "Eye Care"],
      ["nasal-care", "العناية بالأنف", "Nasal Care"],
      ["women-care", "العناية بالمرأة", "Women Care"],
      ["deodorants", "مزيلات العرق", "Deodorants"],
      ["sun-care", "العناية من الشمس", "Sun Care"],
      ["daily-care", "العناية اليومية", "Daily Care"],
      ["oral-care", "العناية بالفم", "Oral Care"],
      ["toothpaste", "معجون الأسنان", "Toothpaste"],
      ["mouth-care", "العناية بصحة الفم", "Mouth Care"],
      ["everyday-hygiene", "النظافة اليومية", "Everyday Hygiene"],
      [
        "medical-supplies",
        "الأجهزة والمستلزمات الصحية",
        "Medical Devices & Health Supplies",
      ],
    ].map(([slug, ar, en]) => ({ slug, ar, en })),
  },
  {
    slug: "mother-baby",
    ar: "الأم والطفل",
    en: "Mother & Baby",
    descriptionAr: "احتياجات الأم والطفل مرتبة بحسب مراحل الرعاية اليومية.",
    descriptionEn:
      "Mother and baby essentials organized around everyday care stages.",
    subcategories: [
      ["baby-formula", "حليب الأطفال", "Baby Formula"],
      ["baby-diapers", "حفاضات الأطفال", "Baby Diapers"],
      ["baby-skin-care", "العناية ببشرة الطفل", "Baby Skin Care"],
      ["baby-bath", "استحمام الطفل", "Baby Bath"],
      ["baby-feeding", "تغذية الطفل", "Baby Feeding"],
      ["feeding-bottles", "الرضاعات", "Feeding Bottles"],
      ["pacifiers", "اللهايات", "Pacifiers"],
      [
        "baby-nasal-care",
        "مستلزمات الأنف والتنفس للأطفال",
        "Baby Nasal & Breathing Care",
      ],
      ["kids-wellness", "عناية الأطفال", "Kids Wellness"],
      ["mother-care", "العناية بالأم", "Mother Care"],
      ["pregnancy-care", "العناية أثناء الحمل", "Pregnancy Care"],
      ["postpartum-care", "العناية بعد الولادة", "Postpartum Care"],
      ["breast-pumps", "مضخات الحليب", "Breast Pumps"],
    ].map(([slug, ar, en]) => ({ slug, ar, en })),
  },
];

const includesAny = (value: string, terms: string[]) =>
  terms.some((term) => value.includes(term));

const supplementSubcategory = (name: string, id: string) => {
  if (id === "9073") return "kids-supplements";
  if (includesAny(name, ["أوميغا", "زيت كبد الحوت", "سكوالين", "زيت القنب"]))
    return "omega-fatty-acids";
  if (includesAny(name, ["أرجينين", "أورنيثين", "حمض أميني"]))
    return "amino-acids";
  if (
    includesAny(name, [
      "مغنيسيوم",
      "زنك",
      "حديد",
      "كالسيوم",
      "كال بلس",
      "كال د",
    ])
  )
    return "minerals";
  if (
    includesAny(name, [
      "سنتروم",
      "سبكترو",
      "فيتامينات متعددة",
      "الفيتامينات الشاملة",
    ])
  )
    return "multivitamins";
  if (
    includesAny(name, [
      "أشواغاندا",
      "قرفة",
      "خل التفاح",
      "كركمين",
      "سيستانش",
      "إشنسا",
    ])
  )
    return "herbal-supplements";
  if (includesAny(name, ["رياضي", "سبورتس", "زيادة الوزن"]))
    return "sports-supplements";
  if (includesAny(name, ["الطاقة", "إنير-جي"])) return "energy-supplements";
  if (name.includes("فيتامين")) return "vitamins";
  return "nutritional-supplements";
};

export const normalizeProduct = (product: Product): Product => {
  let category = product.category;
  let subcategory = product.subcategory;

  if (category === "daily-care") category = "beauty-care";
  if (category === "medical-health") {
    category = "beauty-care";
    subcategory = "medical-supplies";
  }

  if (
    ["vitamins", "care"].includes(category) ||
    (category === "supplements" && subcategory === "supplements")
  ) {
    category = "supplements";
    subcategory = supplementSubcategory(product.nameAr, product.id);
  }
  if (product.id === "9073") subcategory = "kids-wellness";
  if (product.id === "9052") {
    category = "beauty-care";
    subcategory = "medical-supplies";
  }
  if (product.id === "8998") subcategory = "medical-supplies";
  if (product.id === "9470") subcategory = "body-care";
  if (product.id === "9469") subcategory = "hair-care";
  if (product.id === "9466") subcategory = "face-care";
  if (["9463", "9462", "9461", "9460", "9459", "9458"].includes(product.id)) {
    subcategory = "women-care";
  }

  const definition = categoryDefinitions.find((item) => item.slug === category);
  return {
    ...product,
    category,
    subcategory,
    categoryAr: definition?.ar || product.categoryAr,
  };
};

export const categoryBySlug = (slug?: string) =>
  categoryDefinitions.find((category) => category.slug === slug);
